import { linkToSubBasins, approximateGeometryAreaKm2, type SubBasinLayer } from "./gisLayer.ts";
import { disambiguateMitecoNames, type MitecoBasinProperties } from "./mitecoLayers.ts";
import { NO_MODEL, nextId, updateSubBasins, deleteEntitySeriesColumn } from "./entityHelpers.ts";
import { importEntitiesFromGeo } from "./geoImport.ts";
import { addZone, assignZoneInBulk } from "./zoneEntities.ts";
import { addOutletPoint, assignSurfaceOutletInBulk } from "./outletPointEntities.ts";
import type { DailySeries } from "./dailySeries.ts";
import type { SubBasin, EvalHidProject } from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";

export interface SubBasinColumnMapping {
  /** .dbf column with each sub-basin's name/code (e.g. "CodMasa"). */
  nameField: string;
  /** .dbf column with the area in km² (validated as numeric, like InfoAgregarSubCuenc.vb). */
  areaField: string;
}

/** .dbf fields that can be candidates for name or area (numeric for area, any for name). */
export function mappingCandidates(geojson: GeoJSON.FeatureCollection): {
  textFields: string[];
  numericFields: string[];
} {
  const props = geojson.features[0]?.properties ?? {};
  const textFields = Object.keys(props);
  const numericFields = textFields.filter((c) => typeof props[c] === "number");
  return { textFields, numericFields };
}

/** Fields common to a new sub-basin, whether it comes from a shapefile or is created by hand. */
function newSubBasin(
  subBasinCode: number,
  description: string,
  area: number,
  gisCode: number | null
): SubBasin {
  return {
    subBasinCode,
    area,
    precipCol: 0,
    etpCol: 0,
    tempCol: 0,
    pumpingCol: 0,
    partialResults: 0,
    precipAdjustment: 1,
    etpAdjustment: 1,
    tempAdjustment: 1,
    modelTypeCode: NO_MODEL,
    outletCode: null,
    aquiferCode: 0,
    snowModelTypeCode: NO_MODEL,
    gisCode,
    description,
    zoneCode: null,
    snowZoneCode: null,
  };
}

/**
 * Imports the sub-basins from an already-parsed shapefile (see gisLayer.ts,
 * parseShapefile). Generates sequential subBasinCode/gisCode (1..n) — no
 * .dbf field is used as an internal key, so the link with the map never
 * depends on the user-uploaded data being unique.
 */
export function importSubBasinsFromShapefile(
  project: EvalHidProject,
  geojson: GeoJSON.FeatureCollection,
  mapping: SubBasinColumnMapping,
  t: Translate
): { project: EvalHidProject; layer: SubBasinLayer } {
  const LINK_FIELD = "__codGis__";

  const subBasins: SubBasin[] = geojson.features.map((f, i) => {
    const gisCode = i + 1;
    const props = f.properties ?? {};
    const name = String(
      props[mapping.nameField] ?? t("projectEntities.defaultSubBasinName", { n: gisCode })
    );
    return newSubBasin(gisCode, name, Number(props[mapping.areaField]) || 0, gisCode);
  });

  const geojsonWithLink: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: geojson.features.map((f, i) => ({
      ...f,
      properties: { ...f.properties, [LINK_FIELD]: i + 1 },
    })),
  };

  return {
    project: { ...project, subBasins },
    layer: linkToSubBasins(geojsonWithLink, subBasins, LINK_FIELD),
  };
}

/**
 * Creates sub-basins without a shapefile (name + area, by hand) — for when
 * GIS isn't available. Without `gisCode` (there's nothing to link them to),
 * so they don't produce a map layer; they're shown with SubBasinCanvas
 * instead of SubBasinMap.
 */
export function createSubBasinsManually(
  project: EvalHidProject,
  entries: { name: string; area: number }[]
): EvalHidProject {
  const subBasins = entries.map((e, i) => newSubBasin(i + 1, e.name, e.area, null));
  return { ...project, subBasins };
}

/**
 * Adds ONE manual sub-basin to a project that already exists (unlike
 * `createSubBasinsManually`, which replaces the whole array and is only
 * used in step 1 of the wizard). Without `gisCode`: it doesn't show up on
 * the map, only in SubBasinCanvas — same as the ones created by hand from
 * the wizard.
 */
export function addManualSubBasin(
  project: EvalHidProject,
  name: string,
  area: number
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptySubBasinName");
  const subBasinCode = nextId(project.subBasins, (c) => c.subBasinCode);
  const subBasin = newSubBasin(subBasinCode, cleanName, area, null);
  return { ...project, subBasins: [...project.subBasins, subBasin] };
}

/**
 * Parses a small "one sub-basin per row" CSV (its code/name + area) — for
 * bulk-creating sub-basins without a shapefile (the wizard's Step 1 "sin
 * GIS" flow, and SubBasinsPanel.tsx's "Add manually" section) instead of
 * typing each one by hand. Header required, column names matched
 * case-insensitively (`codigo`/`código`/`code`/`nombre`/`name` for the name,
 * `superficie`/`area`/`área` for the area) so it doesn't matter which of the
 * two the file actually uses — in this app's real projects a sub-basin's
 * "name" is typically just its numeric code anyway (e.g. "40", "51"), so
 * "código" and "nombre" are the same field in practice, see SubBasin.description.
 *
 * Accepts either `,` or `;` as the separator (auto-detected from the header
 * row): this app's own precipitation/ETP CSV format always uses `;`
 * (Spanish Excel's default, since `,` is the decimal separator there —
 * dailySeries.ts), but a hand-typed file or one from an English-locale
 * spreadsheet is just as likely to use `,`, so both are accepted rather than
 * forcing one.
 */
export function parseSubBasinsCsv(text: string): { name: string; area: number }[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) throw new TranslatableError("projectEntities.emptyCsv");
  const separator = lines[0].includes(";") ? ";" : ",";
  const header = lines[0].split(separator).map((h) => h.trim().toLowerCase());
  const nameIndex = header.findIndex((h) =>
    ["codigo", "código", "code", "nombre", "name"].includes(h)
  );
  const areaIndex = header.findIndex((h) => ["superficie", "area", "área"].includes(h));
  if (nameIndex === -1 || areaIndex === -1) {
    throw new TranslatableError("projectEntities.csvColumnsNotFound");
  }
  return lines
    .slice(1)
    .map((line) => {
      const fields = line.split(separator);
      return {
        name: (fields[nameIndex] ?? "").trim(),
        area: Number((fields[areaIndex] ?? "0").trim().replace(",", ".")) || 0,
      };
    })
    .filter((e) => e.name !== "");
}

/** The exact inverse of `parseSubBasinsCsv` — one row per sub-basin, `;`-separated to match this app's own precipitation/ETP CSV convention (dailySeries.ts). Meant as a quick reference/template: the "nombre" column is exactly what has to match a precipitation/ETP CSV's own column headers (they're linked by name, see PasteDailySeries.tsx/dailySeries.ts), so exporting this first avoids typos when building that file by hand. */
export function subBasinsToCsv(project: EvalHidProject): string {
  const rows = project.subBasins.map((c) => `${c.description};${c.area}`);
  return ["nombre;superficie", ...rows].join("\r\n");
}

/**
 * Adds the sub-basins from a shapefile to a project that already has
 * sub-basins (unlike `importSubBasinsFromShapefile`, which replaces the
 * whole array and is only used in step 1 of the wizard). `subBasinCode` and
 * `gisCode` continue the existing numbering, so they don't collide with the
 * ones already there. Returns only the layer for the NEW features — the
 * caller (the UI) has to concatenate it with the existing `gisLayer.features`
 * to refresh the whole map.
 *
 * `existingGeometries` (optional): `geometryKey()` keys already present
 * in the project — features whose geometry matches are discarded outright.
 * Meant for "recover from GIS" after a permanent delete (see
 * projectEntities.ts/deleteSubBasin): re-importing the original full shapefile
 * this way avoids duplicating the sub-basins that were still there and
 * didn't need recovering.
 */
export function importAdditionalSubBasins(
  project: EvalHidProject,
  geojson: GeoJSON.FeatureCollection,
  mapping: SubBasinColumnMapping,
  t: Translate,
  existingGeometries?: ReadonlySet<string>
): { project: EvalHidProject; newLayer: SubBasinLayer } {
  const firstCode = nextId(project.subBasins, (c) => c.subBasinCode);
  const { newEntities: newSubBasins, newLayer } = importEntitiesFromGeo(
    geojson,
    existingGeometries,
    project.subBasins,
    (c) => c.gisCode,
    (f, gisCode, i) => {
      const props = f.properties ?? {};
      const name = String(
        props[mapping.nameField] ?? t("projectEntities.defaultSubBasinName", { n: gisCode })
      );
      return newSubBasin(firstCode + i, name, Number(props[mapping.areaField]) || 0, gisCode);
    },
    linkToSubBasins
  );

  const subBasins = [...project.subBasins, ...newSubBasins];
  return { project: { ...project, subBasins }, newLayer };
}

/**
 * Basin's own display label: its `name`, unless MITECO didn't really give
 * it one — confirmed against the real catalog that a basin without a name
 * has `name` set to its own `basinCode` (e.g. `{basinCode: "014001",
 * name: "014001"}`), not an empty string — in which case the code itself
 * is the only thing left to name it after.
 */
function mitecoBasinLabel(props: MitecoBasinProperties): string {
  return props.name && props.name !== props.basinCode ? props.name : props.basinCode;
}

/**
 * MITECO-basin wizard shortcut (Step1SubBasins.tsx's "MITECO" tab): imports
 * every sub-basin found inside a selected basin (`subBasinsForBasin`,
 * mitecoLayers.ts), then creates one HBV zone and one outlet point named
 * after the basin and assigns both to all of them — the 3 separate steps a
 * manual import would otherwise take (import, create zone + assign, create
 * outlet + assign), collapsed into one call since there's exactly one basin
 * involved and nothing to choose between. `subBasinOrigins` maps each new
 * sub-basin to the MITECO sub-basin `basinCode` it came from, so the caller
 * can record it (App.tsx's `mitecoOrigin`) the same way manually adding from
 * MITECO already does — without it, "Add from MITECO" could re-add the same
 * sub-basin a second time later without any warning (see MitecoOrigin,
 * storage.ts).
 *
 * Runs `disambiguateMitecoNames` first, same as `addSelectedMiteco` (App.tsx)
 * does for the manual multi-select flow — MITECO names sub-basins after the
 * river/stream they drain, not the catchment itself, so the same name (e.g.
 * "VINALOPO") is very commonly shared by several sub-basins under the same
 * basin. Without disambiguating, two sub-basins named identically would make
 * `insertColumnInSeries` (which links a series column to a sub-basin by
 * exact name) ambiguous about which one an AEMET fetch's result belongs to.
 */
export function addFromMitecoBasin(
  project: EvalHidProject,
  basin: GeoJSON.Feature,
  subBasins: GeoJSON.Feature[],
  t: Translate
): { project: EvalHidProject; layer: SubBasinLayer; subBasinOrigins: Record<number, string> } {
  const label = mitecoBasinLabel(basin.properties as MitecoBasinProperties);

  const existingNames = new Set(project.subBasins.map((sb) => sb.description));
  const disambiguated = disambiguateMitecoNames(subBasins, existingNames);

  const geojson: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: disambiguated.map((f) => ({
      ...f,
      properties: {
        name: f.properties.name,
        area: approximateGeometryAreaKm2(f.geometry),
      },
    })),
  };
  const beforeCount = project.subBasins.length;
  const { project: withSubBasins, newLayer } = importAdditionalSubBasins(
    project,
    geojson,
    { nameField: "name", areaField: "area" },
    t
  );
  const newSubBasins = withSubBasins.subBasins.slice(beforeCount);
  const subBasinCodes = new Set(newSubBasins.map((c) => c.subBasinCode));
  const subBasinOrigins: Record<number, string> = {};
  newSubBasins.forEach((sb, i) => {
    subBasinOrigins[sb.subBasinCode] = disambiguated[i].properties.basinCode;
  });

  const hbv = withSubBasins.modelTypes.find((m) => m.modelTypeName === "HBV" && m.kind === 0);
  if (!hbv) throw new TranslatableError("projectEntities.hbvModelNotFound");

  let result = addZone(withSubBasins, label, hbv.modelTypeCode, 0);
  const zoneCode = result.zones[result.zones.length - 1].zoneCode;
  result = assignZoneInBulk(result, subBasinCodes, "zoneCode", zoneCode);

  result = addOutletPoint(result, label);
  const outletCode = result.outletPoints[result.outletPoints.length - 1].outletCode;
  result = assignSurfaceOutletInBulk(result, subBasinCodes, outletCode);

  return { project: result, layer: newLayer, subBasinOrigins };
}

/**
 * Edits the name, area and calculation adjustments of an already-created
 * sub-basin (from a shapefile or by hand). The 4 adjustments
 * (partialResults/precipAdjustment/etpAdjustment/tempAdjustment) were
 * already in the model from when the real .ehd is read, but until now there
 * was nowhere in the UI to edit them — confirmed in the original
 * (InfoCuencas.vb) that they are real calculation data (they get written to
 * the engine's input file). precipCol/etpCol/tempCol aren't exposed here on
 * purpose (not because they're dead — they are used, see
 * resolveIndicesByName() in dailySeries.ts, real in v1.0 projects): they're
 * always 0 in a project created or edited from the web (which links its
 * columns by name, v2 format), so there's nothing a user needs to touch by
 * hand here — they only arrive with a real value when importing a v1.0
 * .ehd.
 */
export function editSubBasin(
  project: EvalHidProject,
  subBasinCode: number,
  changes: {
    description: string;
    area: number;
    partialResults: boolean;
    precipAdjustment: number;
    etpAdjustment: number;
    tempAdjustment: number;
  }
): EvalHidProject {
  const cleanName = changes.description.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptySubBasinName");
  return {
    ...project,
    subBasins: project.subBasins.map((c) =>
      c.subBasinCode === subBasinCode
        ? {
            ...c,
            description: cleanName,
            area: changes.area,
            partialResults: changes.partialResults ? 1 : 0,
            precipAdjustment: changes.precipAdjustment,
            etpAdjustment: changes.etpAdjustment,
            tempAdjustment: changes.tempAdjustment,
          }
        : c
    ),
  };
}

/**
 * Permanent delete (like Form1.vb/GuardaCuencas in the original): removes
 * the row from the array, geometry included — no undo. If the sub-basin
 * came from a GIS layer, the only way to recover it is to re-import/redraw
 * that geometry (which arrives as a new sub-basin, without whatever
 * configuration it had before: zone, topology, adjustments...). No other
 * table references a sub-basin by its `subBasinCode`, so no cascade is
 * needed here (unlike zone/outlet/aquifer).
 */
export function deleteSubBasin(project: EvalHidProject, subBasinCode: number): EvalHidProject {
  return {
    ...project,
    subBasins: project.subBasins.filter((c) => c.subBasinCode !== subBasinCode),
  };
}

/**
 * Persists the (1-based, 0 = unlinked) index of the precipitation/ETP/
 * temperature/pumping column that belongs to a sub-basin — called when
 * pasting a single series (PasteDailySeries.tsx), immediately, without
 * waiting for the dialog's "Save" (same as it already does with the series
 * itself). See resolveIndicesByName (dailySeries.ts) for the rest of the
 * places where this same index gets resolved.
 */
export function updateSubBasinColumn(
  project: EvalHidProject,
  subBasinCode: number,
  field: "precipCol" | "etpCol" | "tempCol" | "pumpingCol",
  index: number
): EvalHidProject {
  return updateSubBasins(project, new Set([subBasinCode]), (c) => ({ ...c, [field]: index }));
}

/**
 * Batched form of `updateSubBasinColumn` for MANY different per-sub-basin
 * patches at once — one pass over `project.subBasins`, instead of one pass
 * per (sub-basin, field) pair. Real incident (2026-08-25): a large
 * MITECO+ROCIO import (Duero: 1728 sub-basins) called `updateSubBasinColumn`
 * 3x per sub-basin (precipCol/tempCol/etpCol) as each one's download
 * finished — each call is an O(project.subBasins.length) `.map()`
 * (`updateSubBasins` above), so that's ~5,000 full 1728-element passes in a
 * row, real measured main-thread cost alongside `insertColumnsInSeries`'s
 * own incident note (dailySeries.ts). `aemetApply.ts`'s
 * `applyBatchToProject` collects every sub-basin's 3 column-index patches
 * from one flushed batch into a single `updates` map and applies them all
 * in this one pass.
 */
export function updateSubBasinColumns(
  project: EvalHidProject,
  updates: ReadonlyMap<number, Partial<SubBasin>>
): EvalHidProject {
  if (updates.size === 0) return project;
  return {
    ...project,
    subBasins: project.subBasins.map((c) => {
      const patch = updates.get(c.subBasinCode);
      return patch ? { ...c, ...patch } : c;
    }),
  };
}

/** Sub-basin variant of deleteEntitySeriesColumn — precipitation/ETP/temperature/pumping (SubBasin.precipCol/etpCol/tempCol/pumpingCol), see PasteDailySeries.tsx's "Delete series". */
export function deleteSubBasinSeriesColumn(
  project: EvalHidProject,
  series: DailySeries,
  subBasinCode: number,
  field: "precipCol" | "etpCol" | "tempCol" | "pumpingCol"
): { project: EvalHidProject; series: DailySeries | null; sharedWithOthers: boolean } {
  const result = deleteEntitySeriesColumn(
    project.subBasins,
    series,
    (c) => c.subBasinCode === subBasinCode,
    (c) => c[field],
    (c, index) => ({ ...c, [field]: index })
  );
  return {
    project: { ...project, subBasins: result.entities },
    series: result.series,
    sharedWithOthers: result.sharedWithOthers,
  };
}
